/* ============================================
   MATCHING-ENGINE
   ============================================
   Berechnet den Matchscore (0-100) zwischen
   einer Pflegekraft und einer Einrichtung.

   Finaler Score = Hard-Facts * 0,25
                 + Arbeitsplatz-Fit * 0,60
                 + Retention-Fit * 0,15
                 - Risikoabzüge (max 25)

   Vorab werden No-Go-/Knock-out-Kriterien geprüft.
   ============================================ */

import {
  CATEGORIES,
  SLIDERS,
  SLIDER_BY_ID,
  SWITCH_REASON_BY_ID,
  TOP_REASON_FACTOR,
  slidersForCategory,
  type SliderQuestion,
} from './catalog'
import type {
  MatchCandidate,
  MatchJob,
  MatchResult,
  MatchKategorieV2,
  HardFactDetail,
  CategoryScore,
  SliderDetail,
  RetentionDetail,
  PkOnlyAnswer,
} from './types'
import {
  QUALIFIKATIONEN,
  ARBEITSMODELLE,
  BUNDESLAENDER,
} from '../constants'

/* ---- Label-Helfer für die Detail-Anzeige ---- */
const labelOf = (list: readonly { value: string; label: string }[], v: string | null | undefined) =>
  v ? list.find((i) => i.value === v)?.label || v : '–'
const qLabel = (v: string | null | undefined) => labelOf(QUALIFIKATIONEN, v)
const qListLabel = (v: string[] | null | undefined) =>
  v && v.length ? v.map((x) => qLabel(x)).join(', ') : '–'
const mLabel = (v: string | null | undefined) => labelOf(ARBEITSMODELLE, v)
const mListLabel = (v: string[] | null | undefined) =>
  v && v.length ? v.map((x) => mLabel(x)).join(' / ') : '–'
const bLabel = (v: string | null | undefined) => labelOf(BUNDESLAENDER, v)
const verfShort = (v: string | null | undefined) =>
  v === 'ja' ? 'Ja' : v === 'nein' ? 'Nein' : v === 'optional' ? 'Optional' : '–'
const haeufShort = (v: string | null | undefined) =>
  v === 'ja' ? 'regelmäßig' : v === 'selten' ? 'selten' : v === 'nein' ? 'nein' : '–'
const fmtDate = (v: string | null | undefined) =>
  v ? new Date(v).toLocaleDateString('de-DE') : '–'
const fmtNum = (v: number | null | undefined) =>
  v != null ? v.toLocaleString('de-DE') : '?'

/* ---- Konfiguration ---- */

export const EBENEN_GEWICHT = {
  hardFacts: 0.25,
  arbeitsplatz: 0.6,
  retention: 0.15,
} as const

export const MAX_RISIKO_ABZUG = 25

/** Hard-Fact-Teilgewichte (Summe 100). */
const HARD_FACT_WEIGHTS = {
  qualifikation: 30,
  region: 20,
  arbeitszeit: 20,
  gehalt: 15,
  schicht: 10,
  startdatum: 5,
}

export interface KategorieConfig {
  value: MatchKategorieV2
  label: string
  color: string
  icon: string
  minScore: number
}

export const KATEGORIEN: KategorieConfig[] = [
  { value: 'sehr_stark', label: 'Sehr starker Match', color: '#059669', icon: '🏆', minScore: 90 },
  { value: 'gut', label: 'Guter Match', color: '#10B981', icon: '👍', minScore: 80 },
  { value: 'solide', label: 'Solider Match', color: '#3B82F6', icon: '✅', minScore: 70 },
  { value: 'mit_risiken', label: 'Match mit Risiken', color: '#F59E0B', icon: '⚠️', minScore: 60 },
  { value: 'schwach', label: 'Schwacher Match', color: '#F97316', icon: '🤔', minScore: 50 },
  { value: 'nicht_empfehlenswert', label: 'Nicht empfehlenswert', color: '#94A3B8', icon: '👎', minScore: 0 },
  { value: 'knockout', label: 'Knock-out', color: '#EF4444', icon: '❌', minScore: 0 },
]

export const KATEGORIE_BY_VALUE: Record<MatchKategorieV2, KategorieConfig> = Object.fromEntries(
  KATEGORIEN.map((k) => [k.value, k]),
) as Record<MatchKategorieV2, KategorieConfig>

/* ---- Hilfsfunktionen ---- */

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v))
const round = (v: number) => Math.round(v)

/** Fachkraft-Qualifikationen (können niedrigere Anforderung abdecken). */
const FACHKRAFT = new Set(['pflegefachkraft', 'altenpfleger', 'guk', 'gesundheitspfleger'])
const HILFSKRAFT = new Set(['pflegehilfskraft', 'pflegehelfer'])

/**
 * Slider-Score (0-100) für eine einzelne Frage.
 * Need-Fit:  Arbeitgeber wird nicht bestraft, wenn er mehr bietet als nötig.
 * Präferenz: Nähe zwischen beiden Angaben zählt.
 */
export function sliderScore(slider: SliderQuestion, candVal: number, empVal: number): number {
  if (slider.type === 'preference') {
    const distance = Math.abs(candVal - empVal)
    return clamp(100 - distance * 10, 0, 100)
  }
  // need_fit
  const need = candVal + 5 // 0-10
  const offer = empVal + 5 // 0-10
  if (need <= 0) return 100 // Pflegekraft legt keinen Wert darauf
  if (offer >= need) return 100
  return clamp((offer / need) * 100, 0, 100)
}

/** Need-Fit-Score aus Bedarf (0-10) und Angebot (0-10). */
function needFitFromValues(need: number, offer: number): number {
  if (need <= 0) return 100
  if (offer >= need) return 100
  return clamp((offer / need) * 100, 0, 100)
}

/** Gehaltsangebot der Einrichtung als 0-10-Wert (für Retention-Fit). */
function gehaltOffer(c: MatchCandidate, j: MatchJob): number {
  const wunsch = c.gehaltswunsch_brutto ?? c.mindestgehalt_brutto
  if (!wunsch || j.gehalt_max == null) return 5
  if (wunsch <= j.gehalt_max) return 10
  if (wunsch <= j.gehalt_max * 1.1) return 6
  if (c.mindestgehalt_brutto && c.mindestgehalt_brutto <= j.gehalt_max) return 4
  return 1
}

/* ============================================
   HAUPTFUNKTION
   ============================================ */

export function computeMatch(c: MatchCandidate, j: MatchJob): MatchResult {
  const knockoutReasons: string[] = []
  const warnings: string[] = []
  const strongReasons: string[] = []
  const risks: string[] = []

  const candSliders = c.slider_antworten || {}
  const empSliders = j.slider_antworten || {}
  const noGos = new Set(c.no_gos || [])

  /* ---- 1. KNOCK-OUT-PRÜFUNG ---- */

  // Qualifikation — Einrichtung kann mehrere Qualifikationen akzeptieren
  if (c.qualifikation && j.erforderliche_qualifikationen && j.erforderliche_qualifikationen.length > 0) {
    const list = j.erforderliche_qualifikationen
    if (!list.includes(c.qualifikation)) {
      const candFach = FACHKRAFT.has(c.qualifikation)
      const jobAcceptsHilf = list.some((q) => HILFSKRAFT.has(q))
      if (!(candFach && jobAcceptsHilf)) {
        knockoutReasons.push(
          `Qualifikation passt nicht (gesucht: ${list.join(', ')}, vorhanden: ${c.qualifikation}).`,
        )
      }
    }
  }

  // Region
  if (c.bundesland && j.bundesland && c.bundesland !== j.bundesland && c.umzug_moeglich !== true) {
    knockoutReasons.push('Einrichtung liegt in anderem Bundesland und ein Umzug ist nicht möglich.')
  }

  // Gehalt
  if (c.mindestgehalt_brutto != null && j.gehalt_max != null && c.mindestgehalt_brutto > j.gehalt_max) {
    knockoutReasons.push(
      `Mindestgehalt (${c.mindestgehalt_brutto.toLocaleString('de-DE')} €) liegt über dem Maximalgehalt der Einrichtung (${j.gehalt_max.toLocaleString('de-DE')} €).`,
    )
  }

  // Anerkennung
  if (
    j.anerkennung_erforderlich === 'ja' &&
    (c.anerkennungsstatus === 'in_anerkennung' || c.anerkennungsstatus === 'nicht_begonnen')
  ) {
    knockoutReasons.push('Einrichtung setzt eine abgeschlossene Anerkennung voraus.')
  }

  // Schicht- & No-Go-Konflikte
  if (j.nachtdienst_erforderlich === 'ja' && (noGos.has('keine_nachtdienste') || c.nachtdienst_moeglich === 'nein')) {
    knockoutReasons.push('Nachtdienste sind zwingend erforderlich, von der Pflegekraft aber ausgeschlossen.')
  }
  if (j.geteilte_dienste === 'ja' && (noGos.has('keine_geteilten_dienste') || c.geteilte_dienste_ok === 'nein')) {
    knockoutReasons.push('Geteilte Dienste sind regelmäßig vorgesehen, von der Pflegekraft aber ausgeschlossen.')
  }
  if (j.rufbereitschaft === 'ja' && (noGos.has('keine_rufbereitschaft') || c.rufbereitschaft_ok === 'nein')) {
    knockoutReasons.push('Rufbereitschaft ist fester Bestandteil, von der Pflegekraft aber ausgeschlossen.')
  }
  if (
    j.wochenenddienst_erforderlich === 'ja' &&
    (noGos.has('keine_wochenenddienste') || c.wochenenddienst_moeglich === 'nein')
  ) {
    knockoutReasons.push('Wochenenddienste sind zwingend erforderlich, von der Pflegekraft aber ausgeschlossen.')
  }

  const knockout = knockoutReasons.length > 0

  /* ---- Gelbe Warnhinweise (kein harter Ausschluss) ---- */
  if (j.geteilte_dienste === 'selten' && noGos.has('keine_geteilten_dienste')) {
    warnings.push('Geteilte Dienste kommen selten vor — Pflegekraft möchte diese eigentlich vermeiden.')
  }
  if (j.rufbereitschaft === 'selten' && noGos.has('keine_rufbereitschaft')) {
    warnings.push('Rufbereitschaft kommt selten vor — Pflegekraft möchte diese eigentlich vermeiden.')
  }
  if (j.wochenenddienst_erforderlich === 'selten' && noGos.has('keine_wochenenddienste')) {
    warnings.push('Wochenenddienste kommen selten vor — Pflegekraft möchte diese eigentlich vermeiden.')
  }
  if (j.nachtdienst_erforderlich === 'ja' && c.nachtdienst_moeglich === 'optional') {
    warnings.push('Nachtdienste sind erforderlich — Pflegekraft sieht diese nur als Option.')
  }

  /* ---- 2. HARD FACTS (25 %) ---- */

  const hardFactDetails: HardFactDetail[] = []

  // Qualifikation
  {
    let score = 60
    let status: HardFactDetail['status'] = 'unbekannt'
    let beschr = 'Keine spezifische Anforderung hinterlegt.'
    if (c.qualifikation && j.erforderliche_qualifikationen && j.erforderliche_qualifikationen.length > 0) {
      const list = j.erforderliche_qualifikationen
      if (list.includes(c.qualifikation)) {
        score = 100
        status = 'gut'
        beschr = 'Qualifikation entspricht dem Bedarf der Einrichtung.'
      } else if (FACHKRAFT.has(c.qualifikation) && list.some((q) => HILFSKRAFT.has(q))) {
        score = 90
        status = 'gut'
        beschr = 'Pflegekraft ist höher qualifiziert als gesucht.'
      } else {
        score = 0
        status = 'kritisch'
        beschr = `Gesucht: ${list.join(', ')}, vorhanden: ${c.qualifikation}.`
      }
    } else if (c.qualifikation) {
      score = 80
      status = 'gut'
      beschr = 'Einrichtung hat keinen spezifischen Qualifikations-Bedarf hinterlegt.'
    }
    // Mindestberufserfahrung
    if (j.mindestberufserfahrung != null && c.berufserfahrung_jahre != null) {
      if (c.berufserfahrung_jahre < j.mindestberufserfahrung) {
        score = Math.min(score, 45)
        status = 'teilweise'
        beschr += ` Mindesterfahrung ${j.mindestberufserfahrung} J. nicht erreicht (${c.berufserfahrung_jahre} J.).`
      }
    }
    hardFactDetails.push({
      id: 'qualifikation',
      label: 'Qualifikation & Erfahrung',
      pkAnswer: qLabel(c.qualifikation) + (c.berufserfahrung_jahre != null ? ` · ${c.berufserfahrung_jahre} J. Erfahrung` : ''),
      agAnswer: qListLabel(j.erforderliche_qualifikationen) + (j.mindestberufserfahrung != null ? ` · Mind. ${j.mindestberufserfahrung} J.` : ''),
      score, status, beschreibung: beschr,
    })
  }

  // Region
  {
    let score = 60
    let status: HardFactDetail['status'] = 'unbekannt'
    let beschr = 'Region nicht vollständig angegeben.'
    if (c.bundesland && j.bundesland) {
      if (c.bundesland === j.bundesland) {
        score = 100
        status = 'gut'
        beschr = 'Einrichtung liegt im gewünschten Bundesland.'
      } else if (c.umzug_moeglich === true) {
        score = 70
        status = 'teilweise'
        beschr = 'Anderes Bundesland — Umzug ist für die Pflegekraft möglich.'
      } else {
        score = 0
        status = 'kritisch'
        beschr = 'Anderes Bundesland ohne Umzugsbereitschaft.'
      }
    }
    hardFactDetails.push({
      id: 'region',
      label: 'Region & Anfahrt',
      pkAnswer: bLabel(c.bundesland) + (c.umzug_moeglich ? ' · umzugsbereit' : ''),
      agAnswer: bLabel(j.bundesland),
      score, status, beschreibung: beschr,
    })
  }

  // Arbeitszeit
  {
    const candModelle = c.gewuenschte_arbeitsmodelle || []
    const jobModelle = j.arbeitsmodelle || []
    const modellOk =
      candModelle.length === 0 ||
      jobModelle.length === 0 ||
      candModelle.some((m) => jobModelle.includes(m))
    let stundenScore = 70
    let stundenInfo = 'Wochenstunden nicht eindeutig vergleichbar.'
    if (c.gewuenschte_wochenstunden != null && (j.wochenstunden_min != null || j.wochenstunden_max != null)) {
      const min = j.wochenstunden_min ?? 0
      const max = j.wochenstunden_max ?? 60
      const w = c.gewuenschte_wochenstunden
      if (w >= min && w <= max) {
        stundenScore = 100
        stundenInfo = `Gewünschte ${w} Std./Woche liegen im vorgegebenen Rahmen.`
      } else if (w >= min - 5 && w <= max + 5) {
        stundenScore = 65
        stundenInfo = `Gewünschte ${w} Std./Woche liegen knapp außerhalb des Rahmens.`
      } else {
        stundenScore = 30
        stundenInfo = `Gewünschte ${w} Std./Woche passen nicht zum vorgegebenen Rahmen (${min}-${max}).`
      }
    }
    const score = modellOk ? stundenScore : Math.min(stundenScore, 40)
    const status: HardFactDetail['status'] = score >= 85 ? 'gut' : score >= 50 ? 'teilweise' : 'kritisch'
    const beschr = (modellOk ? '' : 'Arbeitsmodell weicht ab. ') + stundenInfo
    if (score >= 50 && score < 85) warnings.push('Arbeitszeit passt nur teilweise zum vorgegebenen Rahmen.')
    hardFactDetails.push({
      id: 'arbeitszeit',
      label: 'Arbeitszeit & Modell',
      pkAnswer: `${c.gewuenschte_wochenstunden ?? '?'} Std. · ${mListLabel(c.gewuenschte_arbeitsmodelle)}`,
      agAnswer: `${j.wochenstunden_min ?? '?'}–${j.wochenstunden_max ?? '?'} Std. · ${mListLabel(j.arbeitsmodelle)}`,
      score, status, beschreibung: beschr,
    })
  }

  // Gehalt
  let gehaltKnappDrueber = false
  {
    let score = 60
    let status: HardFactDetail['status'] = 'unbekannt'
    let beschr = 'Gehaltsangaben unvollständig.'
    const wunsch = c.gehaltswunsch_brutto ?? c.mindestgehalt_brutto
    if (wunsch != null && j.gehalt_max != null) {
      if (wunsch <= j.gehalt_max) {
        score = 100
        status = 'gut'
        beschr = `Gehaltswunsch (${wunsch.toLocaleString('de-DE')} €) liegt im Rahmen der Einrichtung.`
      } else if (wunsch <= j.gehalt_max * 1.1) {
        score = 65
        status = 'teilweise'
        gehaltKnappDrueber = true
        beschr = `Gehaltswunsch liegt knapp über dem Maximalgehalt (${j.gehalt_max.toLocaleString('de-DE')} €).`
      } else if (c.mindestgehalt_brutto != null && c.mindestgehalt_brutto <= j.gehalt_max) {
        score = 50
        status = 'teilweise'
        beschr = 'Wunschgehalt nicht erreichbar, das Mindestgehalt aber schon.'
      } else {
        score = 10
        status = 'kritisch'
        beschr = 'Gehaltsvorstellung liegt deutlich über dem vorgegebenen Rahmen.'
      }
    }
    hardFactDetails.push({
      id: 'gehalt',
      label: 'Gehalt',
      pkAnswer: `Min ${fmtNum(c.mindestgehalt_brutto)} € · Wunsch ${fmtNum(c.gehaltswunsch_brutto)} €`,
      agAnswer: `${fmtNum(j.gehalt_min)} – ${fmtNum(j.gehalt_max)} €`,
      score, status, beschreibung: beschr,
    })
  }

  // Schicht
  {
    const checks: number[] = []
    const probleme: string[] = []
    const checkShift = (
      required: string | null | undefined,
      avail: string | null | undefined,
      name: string,
    ) => {
      if (required !== 'ja' && required !== 'selten') return
      if (avail === 'ja') checks.push(100)
      else if (avail === 'optional') {
        checks.push(65)
        probleme.push(`${name} nur als Option`)
      } else if (avail === 'nein') {
        checks.push(0)
        probleme.push(`${name} ausgeschlossen`)
      } else checks.push(70)
    }
    checkShift(j.nachtdienst_erforderlich, c.nachtdienst_moeglich, 'Nachtdienst')
    checkShift(j.wochenenddienst_erforderlich, c.wochenenddienst_moeglich, 'Wochenenddienst')
    checkShift(j.geteilte_dienste, c.geteilte_dienste_ok, 'Geteilte Dienste')
    checkShift(j.rufbereitschaft, c.rufbereitschaft_ok, 'Rufbereitschaft')
    const score = checks.length > 0 ? checks.reduce((a, b) => a + b, 0) / checks.length : 90
    const status: HardFactDetail['status'] = score >= 85 ? 'gut' : score >= 50 ? 'teilweise' : 'kritisch'
    const beschr =
      checks.length === 0
        ? 'Keine besonderen Schichtanforderungen.'
        : probleme.length > 0
          ? probleme.join(', ') + '.'
          : 'Alle Schichtanforderungen werden erfüllt.'
    if (score >= 50 && score < 85) warnings.push('Schichtmodell passt nur teilweise.')
    hardFactDetails.push({
      id: 'schicht',
      label: 'Schichten',
      pkAnswer: `Nacht ${verfShort(c.nachtdienst_moeglich)} · WE ${verfShort(c.wochenenddienst_moeglich)} · Geteilt ${verfShort(c.geteilte_dienste_ok)} · Ruf ${verfShort(c.rufbereitschaft_ok)}`,
      agAnswer: `Nacht ${haeufShort(j.nachtdienst_erforderlich)} · WE ${haeufShort(j.wochenenddienst_erforderlich)} · Geteilt ${haeufShort(j.geteilte_dienste)} · Ruf ${haeufShort(j.rufbereitschaft)}`,
      score, status, beschreibung: beschr,
    })
  }

  // Startdatum
  {
    let score = 70
    let status: HardFactDetail['status'] = 'unbekannt'
    let beschr = 'Startzeitpunkt nicht eindeutig.'
    if (j.flexibler_start === true) {
      score = 100
      status = 'gut'
      beschr = 'Flexibler Start möglich.'
    } else if (c.fruehester_wechselzeitpunkt && j.startdatum) {
      const cd = new Date(c.fruehester_wechselzeitpunkt).getTime()
      const jd = new Date(j.startdatum).getTime()
      if (cd <= jd) {
        score = 100
        status = 'gut'
        beschr = 'Pflegekraft kann zum gewünschten Startzeitpunkt verfügbar sein.'
      } else {
        const diffMonate = (cd - jd) / (1000 * 60 * 60 * 24 * 30)
        if (diffMonate <= 2) {
          score = 70
          status = 'teilweise'
          beschr = 'Pflegekraft ist etwas später verfügbar als gewünscht.'
        } else {
          score = 40
          status = 'teilweise'
          beschr = 'Pflegekraft ist deutlich später verfügbar als der gewünschten Startzeitpunkt.'
        }
      }
    } else if (c.fruehester_wechselzeitpunkt) {
      score = 80
      status = 'gut'
      beschr = 'Pflegekraft hat einen frühesten Wechseltermin angegeben.'
    }
    hardFactDetails.push({
      id: 'startdatum',
      label: 'Startzeitpunkt',
      pkAnswer: fmtDate(c.fruehester_wechselzeitpunkt),
      agAnswer: j.flexibler_start ? 'flexibel' : fmtDate(j.startdatum),
      score, status, beschreibung: beschr,
    })
  }

  const hardFactsScore =
    hardFactDetails.reduce((sum, d) => {
      const w = HARD_FACT_WEIGHTS[d.id as keyof typeof HARD_FACT_WEIGHTS] || 0
      return sum + d.score * w
    }, 0) / 100

  /* ---- 3. ARBEITSPLATZ-FIT (60 %) ---- */

  const sliderDetails: SliderDetail[] = []
  let employerAnswered = 0
  // pkOnly-Slider gehen NICHT in den Score ein (z. B. Führungs-Selbsteinschätzung
  // des AG ist zu verzerrt; PK rated trotzdem zur Anzeige im Profil).
  const scorableSliders = SLIDERS.filter((s) => !s.pkOnly)

  for (const slider of scorableSliders) {
    const candRaw = candSliders[slider.id]
    const empRaw = empSliders[slider.id]
    if (empRaw !== undefined) employerAnswered++
    const candVal = candRaw ?? 0
    const empVal = empRaw ?? 0
    sliderDetails.push({
      id: slider.id,
      category: slider.category,
      label: slider.labelCandidate,
      candidateValue: candRaw ?? null,
      employerValue: empRaw ?? null,
      score: sliderScore(slider, candVal, empVal),
    })
  }
  const sliderScoreById: Record<string, number> = Object.fromEntries(
    sliderDetails.map((d) => [d.id, d.score]),
  )

  const categoryScores: CategoryScore[] = CATEGORIES.map((cat) => {
    const sliders = slidersForCategory(cat.id)
    const avg =
      sliders.length > 0
        ? sliders.reduce((sum, s) => sum + (sliderScoreById[s.id] ?? 0), 0) / sliders.length
        : 0
    return {
      categoryId: cat.id,
      label: cat.label,
      icon: cat.icon,
      score: round(avg),
      weight: cat.weight,
    }
  })

  const totalCatWeight = categoryScores.reduce((s, c) => s + c.weight, 0)
  const arbeitsplatzScore =
    totalCatWeight > 0
      ? categoryScores.reduce((s, c) => s + c.score * c.weight, 0) / totalCatWeight
      : 0

  // Arbeitgeberangaben unvollständig?
  const employerCompleteness = scorableSliders.length > 0 ? employerAnswered / scorableSliders.length : 1
  if (employerCompleteness < 0.5) {
    warnings.push('Arbeitgeber hat viele Slider nicht ausgefüllt — Aussagekraft eingeschränkt.')
  }

  /* ---- 4. RETENTION-FIT (15 %) ---- */

  const retentionDetails: RetentionDetail[] = []
  let retentionWeightedSum = 0
  let retentionWeight = 0

  for (const wg of c.wechselgruende) {
    if (!wg.grund || wg.intensitaet < 1) continue
    const reason = SWITCH_REASON_BY_ID[wg.grund]
    const label = reason?.label || wg.grund
    const need = wg.intensitaet * 2 // 0-10

    let offer: number | null = null
    if (reason?.mappedDimension === 'salary_fit') {
      offer = gehaltOffer(c, j)
    } else if (reason?.mappedDimension && SLIDER_BY_ID[reason.mappedDimension]) {
      const empRaw = empSliders[reason.mappedDimension]
      offer = empRaw !== undefined ? empRaw + 5 : 5
    }

    if (offer === null) {
      retentionDetails.push({
        grund: wg.grund,
        label,
        intensitaet: wg.intensitaet,
        topRang: wg.top_rang,
        employerOffer: null,
        score: 0,
        status: 'unbekannt',
        beschreibung: 'Für diesen Wechselgrund gibt es kein direkt prüfbares Einrichtungs-Kriterium.',
      })
      continue
    }

    const score = needFitFromValues(need, offer)
    const topFactor = wg.top_rang ? TOP_REASON_FACTOR[wg.top_rang] || 1 : 1
    const weight = wg.intensitaet * topFactor
    retentionWeightedSum += score * weight
    retentionWeight += weight

    let status: RetentionDetail['status'] = 'neutral'
    let beschr = ''
    if (wg.intensitaet >= 4) {
      if (offer >= 7) {
        status = 'geloest'
        beschr = 'Die Einrichtung adressiert diesen wichtigen Wechselgrund gut.'
      } else if (offer >= 4) {
        status = 'neutral'
        beschr = 'Die Einrichtung adressiert diesen Wechselgrund nur teilweise.'
      } else {
        status = 'risiko'
        beschr = 'Achtung: Dieser starke Wechselgrund wird an der Einrichtung kaum gelöst.'
      }
    } else {
      beschr = offer >= 6 ? 'Wechselgrund wird an der Einrichtung gut abgedeckt.' : 'Wechselgrund wird nur mäßig abgedeckt.'
    }

    retentionDetails.push({
      grund: wg.grund,
      label,
      intensitaet: wg.intensitaet,
      topRang: wg.top_rang,
      employerOffer: offer,
      score: round(score),
      status,
      beschreibung: beschr,
    })
  }

  const retentionScore = retentionWeight > 0 ? retentionWeightedSum / retentionWeight : 65

  /* ---- 5. RISIKOABZÜGE ---- */

  let riskPenalty = 0
  for (const rd of retentionDetails) {
    if (rd.intensitaet === 5 && rd.employerOffer != null && rd.employerOffer < 5) {
      riskPenalty += 10
      risks.push(`Kritischer Wechselgrund „${rd.label}" wird an der Einrichtung nicht gelöst.`)
    } else if (rd.status === 'risiko') {
      riskPenalty += 5
      risks.push(`Wechselgrund „${rd.label}" wird an der Einrichtung nur schwach adressiert.`)
    }
  }
  if (gehaltKnappDrueber) {
    riskPenalty += 5
    risks.push('Gehaltswunsch liegt knapp über dem vorgegebenen Rahmen.')
  }
  const arbeitszeitDetail = hardFactDetails.find((d) => d.id === 'arbeitszeit')
  if (arbeitszeitDetail && arbeitszeitDetail.status === 'teilweise') riskPenalty += 5
  const schichtDetail = hardFactDetails.find((d) => d.id === 'schicht')
  if (schichtDetail && schichtDetail.status === 'teilweise') riskPenalty += 5
  if (employerCompleteness < 0.5) riskPenalty += 8

  riskPenalty = Math.min(riskPenalty, MAX_RISIKO_ABZUG)

  /* ---- 6. FINALER SCORE ---- */

  let totalScore =
    hardFactsScore * EBENEN_GEWICHT.hardFacts +
    arbeitsplatzScore * EBENEN_GEWICHT.arbeitsplatz +
    retentionScore * EBENEN_GEWICHT.retention -
    riskPenalty
  totalScore = clamp(round(totalScore), 0, 100)

  // Bei Knock-out wird kein hoher Score angezeigt.
  if (knockout) totalScore = Math.min(totalScore, 30)

  const kategorie: MatchKategorieV2 = knockout ? 'knockout' : kategorieFromScore(totalScore)

  /* ---- 7. ERKLÄRUNG: starke Gründe, Risiken, Gesprächsimpulse ---- */

  for (const cs of categoryScores) {
    if (cs.score >= 85) strongReasons.push(`${cs.label}: Einrichtung erfüllt die Wünsche sehr gut (${cs.score}/100).`)
  }
  for (const rd of retentionDetails) {
    if (rd.status === 'geloest') {
      strongReasons.push(`Wechselgrund „${rd.label}" wird an der Einrichtung gut adressiert.`)
    }
  }
  const gehaltDetail = hardFactDetails.find((d) => d.id === 'gehalt')
  if (gehaltDetail && gehaltDetail.status === 'gut') strongReasons.push('Gehaltswunsch liegt im Rahmen der Einrichtung.')
  const regionDetail = hardFactDetails.find((d) => d.id === 'region')
  if (regionDetail && regionDetail.score === 100) strongReasons.push('Einrichtung liegt in der gewünschten Region.')
  if (
    c.gewuenschte_bereiche &&
    j.einsatzbereiche &&
    c.gewuenschte_bereiche.some((b) => j.einsatzbereiche!.includes(b))
  ) {
    strongReasons.push('Gewünschter Einsatzbereich liegt im Bedarf der Einrichtung.')
  }

  for (const cs of categoryScores) {
    if (cs.score < 55) risks.push(`${cs.label}: Einrichtung erfüllt die Wünsche nur schwach (${cs.score}/100).`)
  }

  const gespraechsimpulse = buildGespraechsimpulse(c, j, sliderScoreById, retentionDetails, gehaltKnappDrueber)

  // PK-only-Slider: nur PK-Bewertung, kein Score-Beitrag — fürs Profil & PDF.
  const pkOnlyAnswers: PkOnlyAnswer[] = []
  for (const s of SLIDERS) {
    if (!s.pkOnly) continue
    const value = candSliders[s.id]
    if (value === undefined) continue
    pkOnlyAnswers.push({ id: s.id, label: s.labelCandidate, category: s.category, value })
  }

  return {
    knockout,
    knockoutReasons,
    totalScore,
    hardFactsScore: round(hardFactsScore),
    arbeitsplatzScore: round(arbeitsplatzScore),
    retentionScore: round(retentionScore),
    riskPenalty,
    kategorie,
    hardFactDetails,
    categoryScores,
    sliderDetails,
    pkOnlyAnswers,
    retentionDetails,
    strongReasons,
    risks,
    warnings,
    gespraechsimpulse,
  }
}

function kategorieFromScore(score: number): MatchKategorieV2 {
  if (score >= 90) return 'sehr_stark'
  if (score >= 80) return 'gut'
  if (score >= 70) return 'solide'
  if (score >= 60) return 'mit_risiken'
  if (score >= 50) return 'schwach'
  return 'nicht_empfehlenswert'
}

function buildGespraechsimpulse(
  c: MatchCandidate,
  j: MatchJob,
  sliderScoreById: Record<string, number>,
  retentionDetails: RetentionDetail[],
  gehaltKnappDrueber: boolean,
): string[] {
  const impulse: string[] = []
  if ((sliderScoreById['no_free_time_callin'] ?? 100) < 70 || (sliderScoreById['schedule_reliability'] ?? 100) < 70) {
    impulse.push('Wie häufig kommt Einspringen aus der Freizeit tatsächlich vor?')
  }
  if ((sliderScoreById['overtime_control'] ?? 100) < 70) {
    impulse.push('Wie wird mit Überstunden konkret umgegangen und wie werden sie ausgeglichen?')
  }
  if (j.nachtdienst_erforderlich === 'ja' && c.nachtdienst_moeglich === 'optional') {
    impulse.push('Kann die Pflegekraft zunächst ohne bzw. mit reduziertem Nachtdienst starten?')
  }
  if ((sliderScoreById['staffing_level'] ?? 100) < 70 || (sliderScoreById['workload_level'] ?? 100) < 70) {
    impulse.push('Wie ist die Einrichtung aktuell personell besetzt und wie realistisch ist das Pensum?')
  }
  if (gehaltKnappDrueber) {
    impulse.push('Besteht beim Gehalt Verhandlungsspielraum oder gibt es Zulagen/Entwicklungsstufen?')
  }
  if (retentionDetails.some((r) => r.status === 'risiko')) {
    impulse.push('Welche konkreten Maßnahmen gibt es für die wichtigsten Wechselgründe der Pflegekraft?')
  }
  impulse.push('Wie sieht die Einarbeitung in den ersten 30 Tagen konkret aus?')
  return impulse.slice(0, 5)
}
